/**
 * Page Access Service
 * Handles page access checking with organization-specific rules
 */

import apiClient from '../../assets/scripts/services/apiClient';

class PageAccessService {
  /**
   * Check if a user has access to a specific page
   * @param {Object} params - The access check parameters
   * @param {number} params.app_registry_id - The application registry ID
   * @param {string} params.page_url - The page URL to check
   * @param {number} params.user_id - The user ID
   * @param {number} params.organization_id - The organization ID (REQUIRED)
   * @param {string} params.user_type - The user type (e.g., 'admin', 'super_user')
   * @returns {Promise<Object>} - Access check result
   */
  async checkPageAccess(params) {
    const { app_registry_id, page_url, user_id, organization_id, user_type } = params;
    
    // Validate required parameters
    if (!app_registry_id) {
      throw new Error('app_registry_id is required for checking page access');
    }
    if (!page_url) {
      throw new Error('page_url is required for checking page access');
    }
    if (!user_id) {
      throw new Error('user_id is required for checking page access');
    }
    if (!organization_id) {
      throw new Error('organization_id is REQUIRED for checking page access');
    }
    if (!user_type) {
      throw new Error('user_type is required for checking page access');
    }
    
    try {
      const response = await apiClient.post('/pages/check-access', {
        app_registry_id,
        page_url,
        user_id,
        organization_id, // NOW REQUIRED
        user_type
      });
      
      return response;
    } catch (err) {
      console.error('Error checking page access:', err);
      throw err;
    }
  }
  
  /**
   * Update page restrictions for a specific organization
   * @param {number} pageId - The page ID
   * @param {number} organizationId - The organization ID (REQUIRED)
   * @param {Object} restrictedAccessOptions - The access restrictions
   * @returns {Promise<Object>} - Updated page data
   */
  async updatePageRestrictions(pageId, organizationId, restrictedAccessOptions) {
    if (!pageId) {
      throw new Error('pageId is required for updating page restrictions');
    }
    if (!organizationId) {
      throw new Error('organization_id is REQUIRED for updating page restrictions');
    }
    
    try {
      const response = await apiClient.patch(`/pages/${pageId}`, {
        organization_id: organizationId, // REQUIRED field
        restricted_access_options: restrictedAccessOptions
      });
      
      return response;
    } catch (err) {
      console.error('Error updating page restrictions:', err);
      throw err;
    }
  }
  
  /**
   * Get page details including organization-specific access rules
   * @param {number} pageId - The page ID
   * @returns {Promise<Object>} - Page data with nested org-specific rules
   */
  async getPageDetails(pageId) {
    if (!pageId) {
      throw new Error('pageId is required for getting page details');
    }
    
    try {
      const response = await apiClient.get(`/pages/${pageId}`);
      
      // Response now contains organization-specific rules nested by org ID:
      // {
      //   "restricted_access_options": {
      //     "8": { "user_types": [...], "user_list": [...] },
      //     "4": { "user_types": [...], "user_list": [...] },
      //     "default": { "user_types": [...], "user_list": [...] }
      //   }
      // }
      
      return response;
    } catch (err) {
      console.error('Error getting page details:', err);
      throw err;
    }
  }
  
  /**
   * Extract access rules for a specific organization from page data
   * @param {Object} page - The page object with restricted_access_options
   * @param {number} organizationId - The organization ID to get rules for
   * @returns {Object} - The access rules for the organization
   */
  getOrgSpecificAccessRules(page, organizationId) {
    if (!page?.restricted_access_options) {
      return { user_types: [], user_list: [] };
    }
    
    const accessOptions = page.restricted_access_options;
    
    // Check for organization-specific rules first
    if (organizationId && accessOptions[organizationId]) {
      return accessOptions[organizationId];
    }
    
    // Fall back to default rules
    if (accessOptions.default) {
      return accessOptions.default;
    }
    
    // Legacy format (direct user_types and user_list)
    if (accessOptions.user_types || accessOptions.user_list) {
      return {
        user_types: accessOptions.user_types || [],
        user_list: accessOptions.user_list || []
      };
    }
    
    return { user_types: [], user_list: [] };
  }
}

export default new PageAccessService();